// Sugalarm — synthetic Now-screen preview overlay (cycle 36, cycle
// 36.1 chrome).
//
// Subscribes to `alarmPreview.ts` via `useAlarmPreview`; renders nothing
// when no preview is active. When a preview *is* active, paints a full
// synthetic Now-screen on top of whatever the user is doing for 5
// seconds, then auto-clears.
//
// The overlay reuses NowScreen.module.css verbatim so future Now-screen
// design changes flow through to previews without parallel edits.
// Urgent levels render the full-screen UrgentOverlay; `low` and `high`
// render their respective banner above the normal Now layout; in_range
// renders the normal layout with the in-range color treatment.
//
// Cycle 36.1: non-urgent previews are now wrapped in a synthetic copy
// of the app's header strip (KioskHeader on kiosk/web variants,
// MobileHeader on mobile). Pre-cycle the preview painted on a blank
// `var(--bg)` viewport — the user reported the screen feeling naked at
// the top.
//
// Real alarms always win: an effect watches `episode.phase` and
// cancels the preview immediately when a real episode goes active.

import { useEffect, useMemo, useState } from "react";

import type { AlarmEpisodeState } from "../../hooks/useAlarmEpisode";
import { useAlarmPreview } from "../../hooks/useAlarmPreview";
import type { FrameVariant } from "../../hooks/useFrameVariant";
import type { TTSState } from "../../hooks/useTTS";
import { useThresholds } from "../../hooks/useThresholds";
import {
  cancelPreview,
  PREVIEW_DURATION_MS,
  type PreviewZone,
} from "../../lib/alarmPreview";
import {
  displayValue,
  fmtAge,
  trendInfo,
  zoneLabel,
  zoneColor,
  zoneOf,
  zoneTone,
} from "../../lib/glucose";
import { selectCurrentGlucose, type DisplayGlucose } from "../../lib/currentGlucose";
import type { Member, Reading, User } from "../../lib/types";
import { HeroValue } from "../glucose/HeroValue";
import { Sparkline } from "../glucose/Sparkline";
import { TrendArrow } from "../glucose/TrendArrow";
import { Pill } from "../ui/Pill";
import nowStyles from "../../screens/now/NowScreen.module.css";
import { HighAlarmBanner } from "./HighAlarmBanner";
import { KioskHeader } from "./KioskHeader";
import { LowAlarmBanner } from "./LowAlarmBanner";
import { UrgentBanner } from "./UrgentBanner";
import { MobileHeader } from "./MobileHeader";
import { UrgentOverlay } from "./UrgentOverlay";

interface Props {
  /** Real alarm-episode state — when non-idle the preview cancels
   *  immediately so the actual urgent overlay can take over. */
  episode: AlarmEpisodeState;
  /** TTS handle for UrgentOverlay (it accepts the prop but no longer
   *  speaks; passed through to keep the interface symmetric). */
  tts: TTSState;
  /** Active member's display name — shown above the synthetic hero so
   *  previews look identical to the real Now screen. Falls back to
   *  "Preview" when no active member resolved. */
  activeMember: Member | null;
  /** Signed-in user — passed to the synthetic MobileHeader so it can
   *  fall through to `user.display_name` when no active member is
   *  resolved, matching the live MobileFrame header behavior. */
  user: User | null;
  /** Display units, threaded from the SettingsProvider. */
  units: "mg/dL" | "mmol/L";
  /** Frame variant — drives kiosk vs. web vs. mobile layout. */
  variant: FrameVariant;
}

// Cycle 105: the header no longer shows a relationship label, but the
// synthetic Now-screen HERO in the preview keeps its name + relationship
// chip (the hero is unchanged this cycle).
function relationshipLabel(m: Member | null): string {
  if (!m) return "PREVIEW";
  if (m.is_self) return "YOU";
  if (m.relationship_label) return m.relationship_label.toUpperCase();
  return "MEMBER";
}

/** Build a flat synthetic reading series for the side-column
 *  Sparkline. 10 readings at preview.sgv, 18 minutes apart, anchored
 *  to now — gives the panel a non-empty look without pretending it's
 *  historical data. */
function buildSyntheticSeries(sgv: number, now: number): Reading[] {
  const out: Reading[] = [];
  for (let i = 9; i >= 0; i--) {
    const ts = new Date(now - i * 18 * 60_000).toISOString();
    out.push({
      id: `preview-${i}`,
      sgv,
      timestamp: ts,
      trend: "Flat",
      trend_rate: 0,
      provider: "preview",
      received_at: ts,
    });
  }
  return out;
}

/** Synthetic AlarmEpisodeState used to drive UrgentOverlay /
 *  LowAlarmBanner / HighAlarmBanner during a preview. Address /
 *  Silence / endEpisode all call `cancelPreview()` so tapping the
 *  in-overlay actions ends the preview early.
 *
 *  Cycle 36.1: `high` joins the alarming branch so HighAlarmBanner
 *  fires on a High preview the same way LowAlarmBanner fires on a
 *  Low preview.
 *
 *  Cycle 42 + cycle 61: optional `snoozed` flag drives the collapsed
 *  urgent state (post-Address) so the new <UrgentBanner> can be
 *  previewed visually for both urgent_low and urgent_high. The live
 *  5-second Test-alarms preview still passes the default (unsnoozed)
 *  so it fires the initial full takeover — matching the real
 *  first-impression for both urgent levels. */
function syntheticEpisode(
  zone: PreviewZone,
  sgv: number,
  snoozed = false,
): AlarmEpisodeState {
  const isAlarming =
    zone === "urgent_low" ||
    zone === "low" ||
    zone === "high" ||
    zone === "urgent_high";
  const level = isAlarming
    ? (zone as "urgent_low" | "low" | "high" | "urgent_high")
    : null;
  const collapsedUrgent =
    snoozed && (zone === "urgent_low" || zone === "urgent_high");
  return {
    phase: collapsedUrgent ? "snoozed" : isAlarming ? "active" : "idle",
    level,
    // Cycle 311.1: the synthetic preview episode "opened" at its zone's
    // level (only the OTA gate reads this; previews never gate installs
    // anyway — the preview surface isn't a kiosk episode).
    startLevel: level,
    sgv: isAlarming ? sgv : null,
    alertId: null,
    audioOn: isAlarming && !collapsedUrgent,
    hasSnoozed: collapsedUrgent,
    silenced: false,
    // Cycle 62: previews don't drive the real re-arm loop; the
    // synthetic episode is `stopped: false` by default. Tapping the
    // synthetic Stop button (or Snooze) just dismisses the preview
    // via `cancelPreview()` — matches the way `address()` already
    // works in synthetic mode.
    stopped: false,
    // Cycle 63: synthetic episodes never drive the real audio loop
    // or the AlarmSilenceGuard floor — previews own their own
    // dismissal via `cancelPreview()`. `alarmActive` is left false
    // so the guard yields entirely on a preview surface.
    alarmActive: false,
    // Cycle 81: satisfy the phase-live control gate so previews paint
    // controls exactly as a real live episode would. `isLive` mirrors
    // the synthetic `phase` (active for low/high, snoozed for a
    // collapsed urgent, idle for in_range) and `episodeLevel` is the
    // synthetic level — no contract change, the preview surfaces gate
    // on these the same way the live ones do.
    isLive: collapsedUrgent || isAlarming,
    episodeLevel: level,
    // Cycle 304: previews are never early-hidden — a preview drives a fixed
    // synthetic zone, so the in-range suppression never applies.
    // Cycle 398 WIDENED the real predicate to cover ACTIVE episodes, which
    // is exactly the state a preview sits in. It stays pinned false here:
    // a synthetic episode is never driven by real readings, so a real
    // in-range reading landing mid-preview must not dismiss it (the preview
    // owns its own dismissal via `cancelPreview()`). The hook enforces the
    // same rule independently — `inRangeStandDown` returns false whenever
    // `synthetic` is set — so this is belt AND braces, not the only guard.
    bannerSuppressedInRange: false,
    // Cycle 398: never stood down, so there is no edge for a consumer to
    // key a one-shot on.
    inRangeStandDownAt: null,
    // Cycle 428: a preview drives a fixed synthetic zone and is never
    // "recovering"; the header keeps its plain level label.
    recoveringFromUrgentLow: false,
    snoozeRemainingMs: collapsedUrgent ? 5 * 60 * 1000 : null,
    // Cycle 391: the preview does NOT get a treating flow — the cycle-36
    // Test-alarms preview is a five-second look at each zone, and a
    // bounded treatment window has nothing to bound inside it. These are
    // the inert values needed to satisfy the episode surface so the
    // preview keeps rendering; `treat` resolves to a refusal code so a
    // stray caller can never open a window against a synthetic alert id.
    treating: false,
    quietWindow: false,
    // Cycle 410: a preview never has a lead window (it is server-authored,
    // gated on a real placement) — so the audible level is the level and
    // "Sound the alarm now" is inert.
    audioLead: false,
    audioLeadUntil: null,
    audioLeadRemainingMs: null,
    audioLeadPending: false,
    audioLeadPendingUntil: null,
    audibleLevel: level,
    // Cycle 424: a preview never latches — there is no server row behind it.
    ascentLatched: false,
    audible: isAlarming && !collapsedUrgent,
    soundNow: async () => null,
    treatingUntil: null,
    treatingRemainingMs: null,
    treatingCount: 0,
    // Cycle 393: a preview never has custody of anything.
    custodyKind: null,
    treatingContactName: null, treatingBy: null, custodyOpenedAt: null, attributionPickerSeconds: 45, lastTreatWindow: () => null,
    backupReach: null,
    // Cycle 404: a preview has no episode and therefore no opt-outs. Pinned
    // null (not `[]`) to say "no server row is bound", which is exactly what
    // a synthetic alert id means.
    episodeOptOuts: null,
    // Cycle 407: a preview never asks a check-in and never hands back.
    checkinQuestion: null,
    checkinExpiresAt: null,
    checkinPending: false,
    checkinEvent: null,
    treatingExtensionCount: 0,
    // Cycle 427: the preview drives no prompt, no help and no dark feed.
    promptKind: null,
    promptDeadline: null,
    helpState: null,
    helpContactName: null,
    noDataQuiet: false,
    checkin: async () => null,
    custodyHandBack: null,
    setTreatingIdentity: async () => {},
    treat: async () => {
      // Preview convention (cycle 36/62): a synthetic control dismisses
      // the preview rather than driving real state — the same thing the
      // synthetic Snooze and Stop already do. Deliberately NOT a preview
      // flow for treating; there is nothing to bound inside five seconds.
      cancelPreview();
      return null;
    },
    address: async () => {
      cancelPreview();
    },
    silence: () => {
      cancelPreview();
    },
    stop: () => {
      cancelPreview();
    },
    stopNow: () => {
      cancelPreview();
    },
    // Cycle 416: a synthetic preview never POSTs, so an ack can never
    // fail here — the surfaces still read the fields.
    ackFailure: null,
    retryAck: () => {},
    endEpisode: () => {
      cancelPreview();
    },
  };
}

export function PreviewOverlay({
  episode,
  tts,
  activeMember,
  user,
  units,
  variant,
}: Props) {
  const preview = useAlarmPreview();
  const thresholds = useThresholds();
  // Tick once a second so the countdown badge updates in real time.
  // Only mounts a timer while a preview is active.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (preview.zone == null) return;
    const id = window.setInterval(() => setTick((n) => n + 1), 250);
    return () => window.clearInterval(id);
  }, [preview.zone, preview.startedAt]);

  // Real alarms always win. Cancel the preview synchronously when the
  // real episode goes non-idle so NowScreen's urgent branch takes
  // over without flickering through a stale preview frame.
  useEffect(() => {
    if (preview.zone != null && episode.phase !== "idle") {
      cancelPreview();
    }
  }, [preview.zone, episode.phase]);

  const syntheticReadings = useMemo(() => {
    if (preview.zone == null || preview.sgv == null) return [];
    return buildSyntheticSeries(preview.sgv, preview.startedAt || Date.now());
  }, [preview.zone, preview.sgv, preview.startedAt]);

  // Cycle 36.1: a one-reading Reading used by the synthetic
  // MobileHeader on the mobile preview branch. Built here so the
  // ISO timestamps freeze at preview-start and don't churn on every
  // render of the 1 Hz countdown tick.
  // Cycle 435: the synthetic header readout is a `DisplayGlucose`, obtained
  // the only way one can be — through the selector. The preview's reading
  // is stamped at preview start and "confirmed" at the same instant, which
  // is honest: it is a value this screen invented one render ago, not a
  // member's reading, and it is by construction current for the length of
  // the preview.
  const previewCurrent: DisplayGlucose | null = useMemo(() => {
    if (preview.zone == null || preview.sgv == null) return null;
    const anchor = preview.startedAt || Date.now();
    const iso = new Date(anchor).toISOString();
    const reading: Reading = {
      id: "preview-latest",
      sgv: preview.sgv,
      timestamp: iso,
      trend: "Flat",
      trend_rate: 0,
      provider: activeMember?.cgm_provider ?? "preview",
      received_at: iso,
    };
    return selectCurrentGlucose({
      memberId: activeMember?.id ?? "preview",
      reading,
      provider: reading.provider,
      nowMs: anchor,
      serverVerdict: null,
      confirmation: { receivedAtMs: anchor, readingTimestampMs: anchor, source: "snapshot" },
      unknownAfterMs: Number.POSITIVE_INFINITY,
    }).displayValue;
  }, [preview.zone, preview.sgv, preview.startedAt, activeMember?.cgm_provider, activeMember?.id]);

  if (preview.zone == null || preview.sgv == null) return null;

  const zone = preview.zone;
  const sgv = preview.sgv;
  // Resolve the rendered zone against live thresholds so the
  // hero / pill colors match what the real Now screen would paint
  // for a reading at `sgv` mg/dL. With sgvForZone's defaults this
  // tautologically returns `zone`, but using zoneOf keeps the same
  // single source of truth the real screen does.
  const renderedZone = zoneOf(sgv, thresholds);
  const tone = zoneTone(renderedZone);
  const trend = trendInfo("Flat", 0);
  const epi = syntheticEpisode(zone, sgv);
  // Cycle 454 (operator decision): the preview follows reality — only an
  // Urgent Low takes over the screen; an Urgent High previews as the banner
  // on the Now layout, like High (HighAlarmBanner carries both, Stop only).
  const isUrgent = zone === "urgent_low";

  const secondsLeft = Math.max(
    0,
    Math.ceil((PREVIEW_DURATION_MS - (Date.now() - preview.startedAt)) / 1000),
  );

  // Hero font sizes mirror NowScreen's per-variant choices.
  const heroFontSize: number | string =
    variant === "kiosk"
      ? "clamp(160px, 38cqw, 224px)"
      : variant === "mobile"
        ? "clamp(120px, 36vw, 184px)"
        : "clamp(96px, 15vw, 200px)";
  const trendSize: number | string =
    variant === "kiosk"
      ? 88
      : variant === "mobile"
        ? "clamp(36px, 11vw, 56px)"
        : "clamp(44px, 6vw, 72px)";

  const flavorClass =
    variant === "kiosk"
      ? nowStyles.kiosk
      : variant === "mobile"
        ? nowStyles.mobile
        : nowStyles.web;
  const gridClass = `${nowStyles.root} ${flavorClass}`;

  return (
    <div
      role="dialog"
      aria-label={`${zoneLabel(renderedZone)} preview`}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 95,
        background: "var(--bg)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        // Cycle 36.2: iOS status bar + home indicator. The overlay
        // is fixed/inset:0 so it draws under the system chrome by
        // default; mirror the cycle-25 MobileFrame outer-container
        // insets so the synthetic header isn't hidden behind the
        // time/wifi/battery on iPhone and the bottom of the overlay
        // doesn't get clipped by the home indicator.
        paddingTop: "env(safe-area-inset-top, 0)",
        paddingBottom: "env(safe-area-inset-bottom, 0)",
      }}
    >
      {/* Auto-clear badge — also the manual cancel button. Stays
          above the Now layout in the top-right; intentionally not
          inside the layout grid so neither the kiosk frame nor the
          mobile tab bar pushes it off-screen. */}
      <button
        type="button"
        onClick={() => cancelPreview()}
        className="tnum"
        style={{
          position: "absolute",
          // Cycle 36.2: keep the chip clear of the iOS notch/status
          // bar even though the parent overlay carries the top inset
          // (the badge is absolutely-positioned relative to the
          // overlay's padding box; without this calc it would sit
          // 12 px below the status-bar-edge instead of 12 px below
          // the inset's bottom).
          top: "calc(12px + env(safe-area-inset-top, 0px))",
          right: 12,
          zIndex: 110,
          appearance: "none",
          background: "var(--bg-elev)",
          color: "var(--ink-2)",
          border: "0.5px solid var(--line-strong)",
          borderRadius: 999,
          padding: "6px 12px",
          fontSize: "var(--type-floor)",
          fontWeight: 700,
          letterSpacing: "0.02em",
          cursor: "pointer",
          minWidth: "20ch",
          textAlign: "center",
          boxShadow: "0 2px 10px rgba(0,0,0,0.18)",
        }}
        aria-label="Cancel preview"
      >
        Preview · auto-clear in {secondsLeft}s
      </button>

      {isUrgent ? (
        // Cycle 187: mount the collapsed <UrgentBanner> beside the
        // full <UrgentOverlay> so the preview path mirrors the live
        // Now screen — an ACTIVE synthetic urgent paints the full
        // takeover (the banner self-gates to null), and a collapsed
        // one (Snoozed or Stopped) yields the takeover and paints the
        // thin banner instead. The live 5-second Test-alarms preview
        // drives the active state, so the five test zones are
        // unchanged; this keeps the surfaces faithful (no forked
        // banner) for any preview that drives a collapsed urgent.
        <>
          <UrgentBanner episode={epi} units={units} variant={variant} />
          <UrgentOverlay episode={epi} tts={tts} />
        </>
      ) : (
        <>
          {/* Cycle 36.1: synthetic header chrome above non-urgent
              previews. Kiosk + web reuse KioskHeader directly with
              synthetic props (no volume slot, no-op speak button,
              zero alert badge). Mobile builds a one-reading Reading
              and passes it into MobileHeader so the collapsed-header
              readout reads as if a fresh frame just landed. */}
          {variant === "mobile" ? (
            <MobileHeader
              user={user}
              current={previewCurrent}
              now={preview.startedAt || Date.now()}
              units={units}
              ageMin={0}
              zone={renderedZone}
              memberName={activeMember?.display_name ?? null}
              memberAvatarId={activeMember?.avatar_id ?? null}
              predictionPill={null}
            />
          ) : (
            <KioskHeader
              current={previewCurrent}
              zone={renderedZone}
              ageMin={0}
              provider={activeMember?.cgm_provider ?? null}
              unackCount={0}
              prediction={null}
              now={preview.startedAt || Date.now()}
              h24={false}
              units={units}
              trendGlyph={trend.glyph}
              volume={undefined}
              memberName={activeMember?.display_name ?? null}
              memberAvatarId={activeMember?.avatar_id ?? null}
            />
          )}
          <LowAlarmBanner episode={epi} units={units} variant={variant} />
          <HighAlarmBanner episode={epi} units={units} variant={variant} />
          <UrgentBanner episode={epi} units={units} variant={variant} />
          <div className={gridClass}>
            <div className={nowStyles.heroCol}>
              <div className={nowStyles.memberHead}>
                <div className={nowStyles.memberName}>
                  {activeMember?.display_name ?? "Preview"}
                </div>
                <div className={nowStyles.memberRel}>
                  {relationshipLabel(activeMember)}
                </div>
              </div>
              <div className={nowStyles.heroMeta}>
                <Pill tone={tone} appearance="status">
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: 999,
                      background: zoneColor(renderedZone),
                    }}
                  />
                  {zoneLabel(renderedZone)}
                </Pill>
                <span style={{ fontSize: 13, color: "var(--ink-3)" }}>
                  {fmtAge(0)}
                </span>
              </div>
              <div
                className={`${nowStyles.heroRow} ${
                  variant === "mobile" ? "" : nowStyles.heroRowCentered
                }`}
              >
                <HeroValue
                  value={displayValue(sgv, units)}
                  zone={renderedZone}
                  stale={false}
                  pulse={false}
                  size={heroFontSize}
                />
                <div
                  className={nowStyles.trendCol}
                  style={{
                    paddingTop: variant === "mobile" ? 8 : 0,
                  }}
                >
                  <TrendArrow
                    glyph={trend.glyph}
                    zone={renderedZone}
                    stale={false}
                    size={trendSize}
                  />
                  {variant === "mobile" && (
                    <div className={nowStyles.trendUnits}>{units}</div>
                  )}
                </div>
              </div>
              <div className={nowStyles.subline}>
                <span style={{ fontSize: 14, fontWeight: 500 }}>
                  {trend.label}
                </span>
              </div>
            </div>

            <div className={nowStyles.sideCol}>
              <div
                className={`${nowStyles.sparkPanel} ${
                  variant === "kiosk" ? nowStyles.sparkPanelFill : ""
                }`}
              >
                <div className={nowStyles.sparkHead}>
                  <span className={nowStyles.sparkLabel}>Preview</span>
                  <span className={`${nowStyles.sparkBadge} tnum`}>
                    {secondsLeft}s
                  </span>
                </div>
                <Sparkline
                  readings={syntheticReadings}
                  thresholds={thresholds}
                  currentZone={renderedZone}
                  height={variant === "mobile" ? 120 : 160}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
